import React, { useEffect } from "react";
import ProductArtwork from "../ProductArtwork/ProductArtwork";
import type { ProductItem } from "../../types/catalog";
import styles from "./ProductDetails.module.css";
import { useI18n } from "../../i18n/I18n";

const categoryContent: Record<ProductItem["type"], { label: string; description: string; feature: string }> = {
  guitar: { label: "Гітара", description: "Виразний інструмент із комфортним грифом і збалансованим звучанням. Добре підійде для репетицій, домашньої практики та живих виступів.", feature: "Зручний профіль грифа" },
  violin: { label: "Смичковий інструмент", description: "Збалансована скрипка з чистою атакою та теплим тембром. Інструмент легко відгукується на динаміку й підходить для навчання та ансамблевої гри.", feature: "Теплий, чистий тембр" },
  saxophone: { label: "Духовий інструмент", description: "Саксофон із впевненою інтонацією та рівним відгуком у всьому діапазоні. Зручна механіка допомагає зосередитися на музиці.", feature: "Точна й плавна механіка" },
  drums: { label: "Ударний інструмент", description: "Надійний комплект із виразною атакою та широким динамічним діапазоном. Підходить для репетицій, запису та концертної роботи.", feature: "Стабільна концертна конструкція" },
};

interface ProductDetailsProps {
  product: ProductItem | null;
  onClose: () => void;
  onAddToCart: (product: ProductItem) => void;
}

function ProductDetails({ product, onClose, onAddToCart }: ProductDetailsProps) {
  const { language, t } = useI18n();
  useEffect(() => {
    if (!product) return;
    const closeOnEscape = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", closeOnEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = "";
    };
  }, [product, onClose]);

  if (!product) return null;
  const englishContent = {
    guitar: { label: "Guitar", description: "An expressive instrument with a comfortable neck and balanced tone, ideal for practice, rehearsals, and live performance.", feature: "Comfortable neck profile" },
    violin: { label: "String instrument", description: "A balanced violin with clear attack and a warm tone, responsive enough for learning and ensemble playing.", feature: "Warm, clear tone" },
    saxophone: { label: "Wind instrument", description: "A saxophone with confident intonation, even response, and comfortable mechanics across its full range.", feature: "Precise, smooth mechanics" },
    drums: { label: "Percussion instrument", description: "A reliable kit with expressive attack and a broad dynamic range for rehearsals, recording, and live work.", feature: "Stable performance-ready build" },
  };
  const content = language === "en" ? englishContent[product.type] : categoryContent[product.type];

  return (
    <div className={styles.overlay} onMouseDown={onClose} role="dialog" aria-modal="true" aria-labelledby="product-title">
      <article className={styles.modal} onMouseDown={(event) => event.stopPropagation()}>
        <button className={styles.close} onClick={onClose} aria-label={t("close")}>×</button>
        <div className={styles.visual}><ProductArtwork product={product} large /></div>
        <div className={styles.content}>
          <p className="eyebrow">{product.brand} · {content.label}</p>
          <h2 id="product-title">{product.title}</h2>
          <p className={styles.description}>{product.description ?? content.description}</p>
          <dl className={styles.specs}>
            <div><dt>{t("manufacturer")}</dt><dd>{product.brand}</dd></div>
            <div><dt>{t("category")}</dt><dd>{content.label}</dd></div>
            <div><dt>{t("feature")}</dt><dd>{content.feature}</dd></div>
            <div><dt>{t("warranty")}</dt><dd>{t("months12")}</dd></div>
          </dl>
          <div className={styles.footer}>
            <div><small>{t("price")}</small><strong>{product.price.toLocaleString(language === "uk" ? "uk-UA" : "en-US")} ₴</strong></div>
            <button className="button" onClick={() => { onAddToCart(product); onClose(); }}>{t("addToCart")}</button>
          </div>
          <p className={styles.note}>{t("freeSetup")}</p>
        </div>
      </article>
    </div>
  );
}

export default ProductDetails;
